import {
  Button,
  NotificationList,
  NotificationListItem,
  ResponsivePopover,
  Text,
  Title,
} from "@ui5/webcomponents-react";
import { useTranslation } from "react-i18next";

import "./NotificationCenter.css";

/** A notification as delivered by the notification centre (ADR-0010/D-8); texts are already translated. */
export interface ShellNotification {
  id: string;
  title: string;
  body?: string;
  severity: "information" | "success" | "warning" | "error";
  /** Already formatted for display. */
  createdAt: string;
  read: boolean;
  /** Route of the app to open, if any. */
  link?: string;
}

export interface NotificationCenterProps {
  open: boolean;
  opener: HTMLElement | null;
  notifications: readonly ShellNotification[];
  onClose: () => void;
  onMarkAllRead?: () => void;
  /** An entry was opened (marks it read and follows its link). */
  onOpen?: (notification: ShellNotification) => void;
}

const severityState = {
  information: "Information",
  success: "Positive",
  warning: "Critical",
  error: "Negative",
} as const;

/**
 * Notification centre in the Shell Bar (decision D14-9: replaces the legacy internal chat), fed from
 * `msg.notification` and pushed live over SignalR by the application.
 */
export function NotificationCenter({
  open,
  opener,
  notifications,
  onClose,
  onMarkAllRead,
  onOpen,
}: NotificationCenterProps) {
  const { t } = useTranslation("ui");
  return (
    <ResponsivePopover
      open={open}
      opener={opener ?? undefined}
      placement="Bottom"
      onClose={onClose}
      className="positive-notification-center"
      data-testid="notification-center"
      accessibleName={t("notifications.title")}
    >
      <div className="positive-notification-header">
        <Title level="H5">{t("notifications.title")}</Title>
        {onMarkAllRead && notifications.some((item) => !item.read) && (
          <Button design="Transparent" onClick={onMarkAllRead}>
            {t("notifications.markAllRead")}
          </Button>
        )}
      </div>
      {notifications.length === 0 ? (
        <Text className="positive-notification-empty" data-testid="notifications-empty">
          {t("notifications.empty")}
        </Text>
      ) : (
        <NotificationList
          onItemClick={(event) => {
            const id = event.detail.item.dataset["id"];
            const item = notifications.find((candidate) => candidate.id === id);
            if (item) onOpen?.(item);
          }}
        >
          {notifications.map((item) => (
            <NotificationListItem
              key={item.id}
              data-id={item.id}
              data-testid="notification-item"
              titleText={item.title}
              read={item.read}
              state={severityState[item.severity]}
              footnotes={<span>{item.createdAt}</span>}
            >
              {item.body}
            </NotificationListItem>
          ))}
        </NotificationList>
      )}
    </ResponsivePopover>
  );
}
